import React from 'react';
import { IdValue } from 'its-pages';
import { Position, StepType } from '@reactour/tour';
import { TourWrapper } from './tour-wrapper';
import ReactMarkdown from 'react-markdown';
import { TourTOC } from './tour-toc';

export type StepName =
  | 'initial'
  | 'settings'
  | 'classroomSelector'
  | 'competencySelector'
  | 'moduleResults'
  | 'analysisType'
  | 'activeTime'
  | 'problemsCompleted'
  | 'interpretTimeAndCompleted'
  | 'identifyIssueCount'
  | 'identifyIssueCompetency'
  | 'identifyIssueTag'
  | 'identifyIssueType'
  | 'identifyIssueExample'
  | 'identifyIssueDemo'
  | 'identifyCard'
  | 'confusionCard'
  | 'confusionTags'
  | 'confusionCount'
  | 'confusionCompetency'
  | 'confusionExample';

export const stepDisplayNames: Record<StepName, string> = {
  initial: 'Initial',
  settings: 'Settings',
  classroomSelector: 'Active Classes and Class Codes',
  competencySelector: 'Active Modules',
  moduleResults: 'Module Results',
  analysisType: 'Report Cards',
  activeTime: 'Active Time',
  problemsCompleted: 'Questions Completed',
  interpretTimeAndCompleted: 'Interpreting Active Time & Questions Completed',
  identifyIssueCount: '',
  identifyIssueCompetency: '',
  identifyIssueTag: '',
  identifyIssueType: '',
  identifyIssueExample: '',
  identifyIssueDemo: '',
  identifyCard: 'Skill Component Identification Card',
  confusionCard: 'Skill Component Confusion Card',
  confusionTags: '',
  confusionCount: '',
  confusionCompetency: '',
  confusionExample: '',
};

// steps to be included in the TOC
export const stepsTOC: StepName[] = [
  'activeTime',
  'classroomSelector',
  'analysisType',
  'problemsCompleted',
  'interpretTimeAndCompleted',
  'identifyCard',
  'confusionCard',
  'competencySelector',
  'moduleResults',
  'settings',
];

export const tourTocHasStep = (stepName: StepName): boolean => {
  return !!stepsTOC.find((step: StepName) => step === stepName);
};

export const findStepIndex = (stepName: StepName, tourSteps: ITSTourSteps) => {
  tourSteps.findIndex((v) => v.stepName === stepName);
};
export interface ITSStepType extends StepType {
  stepName: StepName;
}

const stepStyles = {
  popover: (base: IdValue<any>) => ({
    ...base,
    backgroundColor: 'white',
    borderRadius: '1em',
    maxWidth: 'calc(min(80vw, 400px))',
    padding: '1em',
  }),
  maskArea: (base: IdValue<any>) => {
    return { ...base, rx: 15, ry: 15 };
  },
};

const defaultStep: StepType = {
  selector: 'default',
  content: 'default',
  styles: stepStyles,
};

type StylesObj = typeof defaultStep.styles;

const markdownStep = (
  stepName: StepName,
  markdown: string,
  styles: StylesObj,
  preferredPosition?: Position
): ITSStepType => {
  const selector = `[data-tour='${stepName}'`;
  return {
    position: preferredPosition,
    stepName,
    selector,
    styles,
    content: () => <TourWrapper><ReactMarkdown>{markdown}</ReactMarkdown></TourWrapper>,
  };
};

const initialStep: ITSStepType = {
  stepName: 'initial',
  styles: {
    ...stepStyles,
    maskArea: (base: IdValue<any>) => ({
      ...base,
      width: '0px',
      height: '0px',
      borderRadius: '0px',
    }),
  },
  selector: '[classname="body"]',
  content: (
      <TourWrapper>
        <ReactMarkdown>
          {`## Welcome to the ITS Instructor Dashboard!  
**This tour will show you important features.**  

Click the arrows or press the arrow keys to step through the tour sequentially. To jump to a specific explanation, select the topic from the table of contents below.
`}
        </ReactMarkdown>
        <TourTOC />
      </TourWrapper>),
  position: 'center',
};

const settingsStep = {
  ...markdownStep(
    'settings',
    `## Settings
**Settings for the ITS can be accessed by going to the vertical ellipse on the top right corner of the dashboard.**  
- **Class Mode**: This mode is for viewing the ITS in front of students. It de-identifies student data.
- **Act as Student**: Allows you to see the ITS as your students do.  While your
progress is saved, your results will NOT be logged and will NOT be displayed 
when acting as a teacher.
- **Open Tour**: Opens this tour again for reference.
- **Create Student Link**: Creates a link to sign up for the currently selected class. The link is automatically copied to your clipboard so you can share it with students.`,
    stepStyles
  ),
  mutationObservables: ['[data-tour="settings"]'],
};

const ltiSettingsStep = {
  ...markdownStep(
    'settings',
    `**This is where you access settings for the ITS**  
- **Classroom Mode**: anonymize display to allow for display in front of students
- **Open Tour**: opens this tour again for reference`,
    stepStyles
  ),
  mutationObservables: ['[data-tour="settings"]'],
};

const filterModuleStep = markdownStep(
  'moduleResults',
  `## Module Results
**Module Results can be viewed by selecting this dropdown menu. It displays all modules that have at least one student response to a question in that module.**

Use this menu as a filter to see student data for one module or show all modules simultaneously.`,
  stepStyles
);

const analysisStep = markdownStep(
  'analysisType',
  `## Report Cards
**The report cards for the selected module can be viewed by selecting this dropdown menu.**

Use the drop down to choose the report card data you would like to display.

Currently, there are 2 report card types: 
- **Common mistakes by question type:** Display cards describing the most common issues identifying a skill component for
each question type.
- **Common confusions between components:** Display cards describing which skill components were commonly confused when
displayed together.`,
  stepStyles
);

const activeTimeStep = markdownStep(
  'activeTime',
  `## Active Time
  **Active time is the time students spend on a module.**  
  Long gaps in activity are not counted as it is assumed they are away from their device.`,
  stepStyles
);

const problemsCompletedStep = markdownStep(
  'problemsCompleted',
  `## Questions Completed
  **This displays the number of questions that were presented to the student.**  
  Students that struggle with concepts tend to have more questions presented due
  to the adaptive nature of the system.`,
  stepStyles
);

const interpretTimeAndCompletedStep = markdownStep(
  'interpretTimeAndCompleted',
  `## Interpreting Active Time & Questions Completed 
  
  Some interesting trends to look for are:
  - **Short active time with small completed count** might mean the student was already proficient. 
  - **Short active time with large completed count** might mean the student is not be spending enough time reading and contemplating questions.
  - **Long active time with a small completed count** might mean the student is taking their time and learning. 
  - **Long active time with a large completed count** might mean the student is struggling and may need more direct support.`,
  stepStyles
);

const identifyIssueCountStep = markdownStep(
  'identifyIssueCount',
  `**This displays the number of students that had an issue with the given skill component on the given question type.**  

The filled portion of the semi-circle represents the proportion of the class that had the same issue.`,
  stepStyles,
  'right'
);

const identifyIssueCompetencyStep = markdownStep(
  'identifyIssueCompetency',
  `**The specific skill on which the issue was reported.**  
  
  This will not be present if the issues are filtered to a single skill.`,
  stepStyles,
  'right'
);

const identifyIssueTagStep = markdownStep(
  'identifyIssueTag',
  `**The specific skill component on which the issue was reported.**`,
  stepStyles,
  'right'
);

const identifyIssueTypeStep = markdownStep(
  'identifyIssueType',
  `**The type of question on which the issue was reported.**`,
  stepStyles,
  'right'
);

const identifyIssueExampleStep = markdownStep(
  'identifyIssueExample',
  `**Click here to display example mistakes made by your students.**

  Continue tour for descriptions of report card elements.`,
  stepStyles,
  'right'
);

const identifyIssueDemoStep = markdownStep(
  'identifyIssueDemo',
  `**Click here to try an example of this question type.**
  
  Continue tour for descriptions of report card elements.`,
  stepStyles,
  'right'
);

const identifyIssueStep = markdownStep(
  'identifyCard',
  `**This card type displays data about an issue that students were having identifying a skill component.**  
  
  Continue the tour to get descriptions of the element on the card.`,
  stepStyles,
  'right'
);

const newLocal = `**This card type displays data about an issue where students were confusing two different components of a skill.**  

These are examples of when a student was presented two different components of a skill and choose incorrectly.  

Continue the tour to get descriptions of the element on the card.`;
const confusionCardStep = markdownStep('confusionCard', newLocal, stepStyles, 'left');

const confusionExampleStep = markdownStep(
  'confusionExample',
  `**Click here to display actual examples of student responses displaying this issue.**`,
  stepStyles,
  'left'
);

export type ITSTourSteps = ITSStepType[];
const coreSteps: ITSTourSteps = [
  filterModuleStep,
  analysisStep,
  identifyIssueStep,
  identifyIssueCompetencyStep,
  identifyIssueCountStep,
  identifyIssueTypeStep,
  identifyIssueTagStep,
  identifyIssueExampleStep,
  identifyIssueDemoStep,
  confusionCardStep,
  //confusionTagsStep,
  confusionExampleStep,
  activeTimeStep,
  problemsCompletedStep,
  interpretTimeAndCompletedStep
];

const nonLTISteps: ITSTourSteps = [];//classroomSelectorStep, competencyStep];
export const tourSteps: ITSTourSteps = [
  initialStep,
  ...nonLTISteps,
  ...coreSteps,
  settingsStep,
];

export const ltiTourSteps: ITSTourSteps = [
  initialStep,
  ...coreSteps,
  ltiSettingsStep,
];
